import React from 'react';
import { fmtUsDate } from './format';
import { LEADER_COLUMNS, fmtLeaderValue, barScale, barWidthPct } from './contractMetrics';
import type { ColumnKey, MetricRow } from './contractMetrics';

/**
 * One contract leaderboard: Highest Volume, Highest OI, or Largest OI Changes.
 *
 * ⚠️ **Renders the server's ranking as given. It does not sort.** All three
 * panels are ranked over the whole chain by `/v1/contract-leaders`; re-sorting
 * here would re-rank a list that is already correct, using only the rows that
 * survived the server's cut. See `contractLeadersService.ts` for why the old
 * client-side sort mis-ranked both OI panels.
 */

/* ── panel ──────────────────────────────────────────────────────────── */

/**
 * A row this table can render: contract identity plus whatever metrics the
 * panel's columns ask for. Satisfied structurally by both `ContractLeader`
 * (`/v1/contract-leaders`) and `TopContract` (`/v1/top-contracts`).
 */
export interface LeaderRow extends MetricRow {
  /** `YYYY-MM-DD`. */
  expiry: string;
  /** Capitalised on the wire. */
  option_type: 'Call' | 'Put';
}

interface LeaderProps {
  title: string;
  /** Columns, left to right. */
  columns: ColumnKey[];
  /** The server-ranked rows for this panel. Rendered in the order given. */
  rows: LeaderRow[] | null;
  isLoading?: boolean;
  /** Distinguishes a dead request from a slow one. */
  isError?: boolean;
  hasAccess: boolean;
  symbol: string;
  /**
   * Shown under the heading. The session these rankings cover — which before
   * the open is the PREVIOUS session, so it is never assumed to be today.
   */
  asOf?: string;
  /** How many rows to list. Fifteen by default. */
  limit?: number;
  /**
   * Empty-state copy. Overridden by the OI-change panel, whose empty list
   * means "not diffable yet" rather than "nothing to show".
   */
  empty?: string;
  /**
   * Tints each row from the left, proportionally to this column's value, so the
   * ranking reads at a glance instead of being inferred by comparing formatted
   * numbers down the table.
   *
   * ⚠️ **Scaled to the largest value in the rows passed in, not to a fixed
   * ceiling.** The bar therefore shows each row's share of the top row on THIS
   * panel — it is a within-panel comparison and says nothing about the other
   * panel beside it, whose own top row is also full width. Calls and puts are
   * not comparable by bar length.
   *
   * Off by default; the three leaderboards render without it.
   */
  barColumn?: ColumnKey;
}

// Everything is centred. Mixing right-aligned numbers with a left-aligned
// date pushed Strike and Expiry into each other with a gulf on either side;
// centring every column keeps the gaps even across the whole table.
const HEAD_CLASS = 'px-3 py-2 font-semibold text-gray-400 whitespace-nowrap text-center';
const CELL_CLASS = 'px-3 py-1.5 whitespace-nowrap text-center';

/** Faint enough that the numbers on top keep their contrast in both tones. */
const BAR_ALPHA = 0.22;

/**
 * Bar tints, as literal rgba rather than theme classes.
 *
 * ⚠️ **Raw hex on purpose, and it must be kept in step with `@theme`.** The bar
 * is a gradient colour stop in an inline style, which Tailwind cannot supply —
 * a class only reaches `background-color`, and setting both leaves the class
 * silently overridden. These are `bull-blue` (#1C76A2) and `bear-red` (#D8483B)
 * from `src/index.css`. A theme rename that misses these two leaves the bars on
 * the old palette while everything around them moves; see the note in
 * `app/CLAUDE.md` about raw hexes surviving the `bull-green` → `bull-blue`
 * rebrand undetected, because Tailwind v4 drops unknown tokens without erroring.
 */
const BAR_CALL = `rgba(28, 118, 162, ${BAR_ALPHA})`;
const BAR_PUT = `rgba(216, 72, 59, ${BAR_ALPHA})`;

export function ContractLeaders({
  title,
  columns,
  rows,
  isLoading = false,
  isError = false,
  hasAccess,
  symbol,
  asOf,
  limit = 15,
  empty,
  barColumn,
}: LeaderProps): React.ReactElement {
  const leaders = React.useMemo(() => (rows ?? []).slice(0, limit), [rows, limit]);

  // Scaled over the displayed slice, so the top visible row is always the full
  // bar. See `barScale` for why the fetched-but-cut rows are excluded.
  const scale = React.useMemo(() => barScale(leaders, barColumn), [leaders, barColumn]);

  return (
    <section className="p-4 bg-dark-surface">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">{title}</h2>
        {/* Dated from the response, never the wall clock — pre-open these
            rankings are the previous session's. */}
        {hasAccess && asOf && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-numeric">
            {fmtUsDate(asOf)}
          </span>
        )}
      </div>

      {!hasAccess ? (
        <p className="text-gray-400 text-sm">Contract detail requires a Fuzion subscription.</p>
      ) : isError ? (
        <p className="text-bear-red text-sm">Failed to load contract data</p>
      ) : isLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-5 bg-dark-border rounded" />
          ))}
        </div>
      ) : !leaders.length ? (
        <p className="text-gray-400 text-sm">{empty ?? `No contract activity for ${symbol}`}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-dark-border">
          <table className="w-full text-xs">
            <thead className="bg-dark-surface-light border-b border-dark-border">
              <tr>
                {columns.map((col) => (
                  <th key={col} className={HEAD_CLASS}>
                    {LEADER_COLUMNS[col].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leaders.map((row) => {
                const isCall = row.option_type === 'Call';

                /**
                 * The bar is painted as a hard-stopped linear gradient on the
                 * row itself rather than as a positioned element inside it.
                 * A `<tr>` does not establish a containing block, so an
                 * absolutely-positioned child of a row is laid out against the
                 * nearest positioned ancestor instead of the row — it renders
                 * at inconsistent widths across browsers. A background needs no
                 * containing block and clips to the row exactly.
                 */
                const barPct = barWidthPct(row, barColumn, scale);
                const tint = isCall ? BAR_CALL : BAR_PUT;

                return (
                  <tr
                    key={`${row.expiry}-${row.strike}-${row.option_type}`}
                    className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                    style={
                      barPct == null
                        ? undefined
                        : {
                            backgroundImage: `linear-gradient(to right, ${tint} 0 ${barPct}%, transparent ${barPct}% 100%)`,
                          }
                    }
                  >
                    {columns.map((col) => {
                      if (col === 'type') {
                        return (
                          <td key={col} className={CELL_CLASS}>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] ${
                                isCall
                                  ? 'bg-bull-blue/20 text-bull-blue'
                                  : 'bg-bear-red/20 text-bear-red'
                              }`}
                            >
                              {row.option_type}
                            </span>
                          </td>
                        );
                      }

                      if (col === 'expiry') {
                        return (
                          <td key={col} className={`${CELL_CLASS} font-numeric text-white`}>
                            {fmtUsDate(row.expiry)}
                          </td>
                        );
                      }

                      if (col === 'strike') {
                        return (
                          <td key={col} className={`${CELL_CLASS} font-numeric text-white`}>
                            {row.strike.toFixed(2)}
                          </td>
                        );
                      }

                      const def = LEADER_COLUMNS[col];
                      const value = def.read(row);
                      const tone =
                        value == null
                          ? 'text-gray-600'
                          : def.signed
                            ? value > 0
                              ? 'text-bull-blue'
                              : value < 0
                                ? 'text-bear-red'
                                : 'text-gray-400'
                            : 'text-white';

                      return (
                        <td key={col} className={`${CELL_CLASS} font-numeric ${tone}`}>
                          {/* ⚠️ Null is a dash, never a zero. On the OI columns
                              a rendered 0 would claim the position was
                              unchanged, or built today from nothing — both are
                              assertions the feed did not make. */}
                          {value == null ? '—' : fmtLeaderValue(value, def)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
